import { useEffect, useState } from "react";
import {
  Button,
  ButtonVariant,
  Spinner,
  Text,
  TextField,
  Toggle,
} from "@merge-api/merge-javascript-shared";
import { Plus, Trash2 } from "lucide-react";
import { Dialog } from "@merge-api/merge-javascript-shared/dist/designSystem/molecules/Dialog";
import { showErrorToast, showSuccessToast } from "../../../shared/Toasts";
import { conductorDelete, conductorPut } from "../../conductorApi";
import { useConductorResource } from "../../hooks/useConductorResource";
import { ConductorFlag, ConductorFlagRule, EMPTY_RULE } from "../types";
import ConductorFlagRuleCard from "./ConductorFlagRuleCard";

interface Props {
  flagId: string;
  onDeleted: () => void;
}

const ConductorFlagDetail = ({ flagId, onDeleted }: Props) => {
  // state
  const [reloadToken, setReloadToken] = useState(0);
  const [draft, setDraft] = useState<ConductorFlag | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // hooks
  const { data: flag, isLoading } = useConductorResource<ConductorFlag>(
    `/conductor/flags/${flagId}`,
    "Error fetching flag",
    reloadToken
  );

  // derived state
  const isDirty = !!draft && !!flag && JSON.stringify(draft) !== JSON.stringify(flag);
  const canSave = !!draft && draft.name.trim() !== "" && isDirty && !isSaving;

  // Reset the working copy whenever a fresh flag arrives from the server.
  useEffect(() => {
    if (flag) setDraft(flag);
  }, [flag]);

  // event handlers
  const patch = (changes: Partial<ConductorFlag>) =>
    setDraft((current) => (current ? { ...current, ...changes } : current));

  const patchRule = (index: number, rule: ConductorFlagRule) =>
    patch({ rules: (draft?.rules || []).map((existing, i) => (i === index ? rule : existing)) });

  const removeRule = (index: number) =>
    patch({ rules: (draft?.rules || []).filter((_, i) => i !== index) });

  const save = async () => {
    if (!draft) return;
    setIsSaving(true);
    // Update is a full replacement, so the whole flag goes back every time.
    const { ok, error } = await conductorPut(`/conductor/flags/${flagId}`, draft);
    setIsSaving(false);
    if (ok) {
      showSuccessToast("Flag saved");
      setReloadToken((token) => token + 1);
    } else {
      showErrorToast(error || "Failed to save flag");
    }
  };

  const remove = async () => {
    setIsDeleting(true);
    const { ok, error } = await conductorDelete(`/conductor/flags/${flagId}`);
    setIsDeleting(false);
    setConfirmDelete(false);
    if (ok) {
      showSuccessToast("Flag deleted");
      onDeleted();
    } else {
      showErrorToast(error || "Failed to delete flag");
    }
  };

  // render
  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (!flag || !draft) {
    return (
      <Text variant="md" className="block py-16 text-center text-gray-60">
        Flag not found.
      </Text>
    );
  }

  return (
    <div className="pb-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Text variant="h4">{flag.name}</Text>
          <Text variant="sm" className="mt-1 block font-mono text-gray-60">
            {flag.id}
          </Text>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={save} disabled={!canSave} loading={isSaving}>
            Save changes
          </Button>
          <Button
            size="sm"
            variant={ButtonVariant.DangerText}
            leftIcon={<Trash2 size={14} />}
            onClick={() => setConfirmDelete(true)}
          >
            Delete flag
          </Button>
        </div>
      </div>

      <div className="mb-6 flex flex-col gap-4 rounded-lg border border-gray-10 bg-white p-4">
        <TextField
          labelTitle="Name"
          labelSubtitle="The lookup key conductor evaluates. Renaming is allowed if the new name is free."
          value={draft.name}
          onChange={(event) => patch({ name: event.target.value })}
        />
        <TextField
          labelTitle="Note"
          value={draft.note}
          onChange={(event) => patch({ note: event.target.value })}
          placeholder="what this flag controls"
        />
        <div className="flex items-center justify-between border-t border-gray-10 pt-4">
          <div>
            <Text variant="h6">On for everyone</Text>
            <Text variant="sm" className="text-gray-40">
              Forces the flag on everywhere and skips the rules below
            </Text>
          </div>
          <Toggle
            checked={draft.everyone === true}
            onChange={(value) => patch({ everyone: value })}
          />
        </div>
      </div>

      <div className="mb-3 flex items-center justify-between">
        <div>
          <Text variant="h6">Rules</Text>
          <Text variant="sm" className="text-gray-40">
            A flag matches if any enabled rule matches
          </Text>
        </div>
        <Button
          size="sm"
          variant={ButtonVariant.SecondaryGray}
          leftIcon={<Plus size={12} />}
          onClick={() => patch({ rules: [...draft.rules, { ...EMPTY_RULE }] })}
        >
          Add rule
        </Button>
      </div>

      {draft.rules.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-20 py-10 text-center">
          <Text variant="md" className="text-gray-60">
            {draft.everyone === true
              ? "No rules. The flag is on for everyone."
              : "No rules. The flag is off for everyone."}
          </Text>
        </div>
      ) : (
        draft.rules.map((rule, index) => (
          <ConductorFlagRuleCard
            key={index}
            index={index}
            rule={rule}
            onChange={(next) => patchRule(index, next)}
            onRemove={() => removeRule(index)}
          />
        ))
      )}

      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        variant="sm"
        title="Delete flag?"
        primaryButtonText="Delete flag"
        primaryButtonVariant={ButtonVariant.DangerFilled}
        primaryButtonLoading={isDeleting}
        onPrimaryButtonClick={remove}
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={() => setConfirmDelete(false)}
      >
        <Text variant="md" className="text-gray-70">
          Are you sure you want to delete {flag.name}?
        </Text>
      </Dialog>
    </div>
  );
};

export default ConductorFlagDetail;
